import React from 'react';
import { Link } from 'react-router-dom';
import './Story.css';
import sarbFoodTruck from '../ETD_2206.jpg';
import laurelOpening from '../regional-hub.jpg';
import quarterStation from '../Commissary_FeatureImage-1280x727-1.jpg';
import vancouverMagazine from '../6893783386_37f50f8557_o-2048x1534.jpg';
import SEO from '../components/SEO';

const Story: React.FC = () => {
  return (
    <div className="story-page">
      {/* 772 impr at pos 10.9 with 0.13% CTR — brand/company intent */}
      <SEO
        title="About Commissary Connect | Vancouver Shared Kitchens"
        description="How Commissary Connect built shared commercial kitchen space in Vancouver — pay-for-what-you-use kitchens for food trucks, caterers, bakers and CPG brands."
        keywords="commissary connect, about commissary connect, vancouver commissary kitchen company"
      />
      <div className="hero-section">
        <div className="container">
          <h1>From Food Truck to Provincial Pioneer</h1>
          <p className="hero-subtitle">The story of how one entrepreneur's struggle created BC's entire food hub network</p>
        </div>
      </div>

      <div className="container">
        <div className="story-content">
          <section className="story-section founder-story">
            <div className="founder-intro">
              <p className="story-lead">
                As CEO and Founder, Sarb launched Commissary Connect in 2012 after struggling to find a suitable 
                shared kitchen space for his first entrepreneurial venture, a food truck called the <strong>Soho Road Naan Kebab</strong>.
              </p>
              <p>
                Although he loved running the food truck and learning to cook in a traditional tandoor oven, Sarb was 
                frustrated by not having the ability to rent a kitchen space and only pay for the specific equipment that he used.
              </p>
            </div>
            <div className="story-image">
              <img src={vancouverMagazine} alt="Vancouver Magazine cover featuring Sarb Mund" />
              <p className="image-caption">Sarb Mund serving from Soho Road Naan Kebab<br/>c. April 02, 2012</p>
            </div>
          </section>

          <section className="story-section vision-section">
            <h2>The Vision: More Than Just a Kitchen</h2>
            <p>
              However, Sarb did not want to just be a landlord. His vision went far beyond renting cooking stations—he wanted 
              to create a <strong>food business incubator</strong> that collaboratively connected people in the food 
              industry all across the entire province. He envisioned a space where food entrepreneurs would become members 
              and connect with each other while growing their businesses.
            </p>
            <p>
              From the beginning, he was determined to play a significant role in creating a <strong>resilient and thriving 
              food ecosystem in British Columbia</strong>. In 2017, he opened a second location a few doors down from the 
              original kitchen in East Vancouver. This space was specifically designed to meet the needs of bakers and 
              others focused on sweeter preparations.
            </p>
            <div className="quote-callout">
              <blockquote>"Culinary Crusader"</blockquote>
              <cite>— Vancouver Magazine once described Sarb Mund, who is the driving force behind Commissary Connect</cite>
              <p style={{marginTop: '1rem', fontSize: '0.95rem', color: '#666'}}>
                Little did they know then how prophetic that statement would be just a few years later.
              </p>
            </div>
            <div className="story-image">
              <img src={quarterStation} alt="Quarter station in Commissary Connect facility" />
              <p className="image-caption">Sarb Mund, alongside like-minded entrepreneurs featured on the cover of Vancouver Magazine<br/>c. 2017</p>
            </div>
          </section>

          <section className="story-section achievement-section">
            <h2>Building BC's Food Infrastructure</h2>
            <p>
              By 2019, Commissary Connect was ready to grow again and a third location was constructed on Laurel Street 
              in Vancouver. Built to the highest level of food safety standards, this kitchen is bigger than the other 
              two—catering specifically to food producers who are ready to scale their production and grow their business.
            </p>
            <p>
              The Laurel Street location is also the <strong>first Shared-Use Hazard Analysis and Critical Control Points
              (HACCP) ready site in Canada</strong>, and <strong>BC's first Regional Food Hub and Pilot and Demonstration 
              Site for the Provincial Food Hub Network</strong>. The BC Food Hub Network (Network) is designed to support 
              growth and innovation in the processing sector through improved industry access to facilities, equipment, 
              technology, technical services and business resources.
            </p>
            <div className="story-image">
              <img src={laurelOpening} alt="Laurel Street location opening" />
              <p className="image-caption">Opening of the Laurel Street location<br/>c. 2019</p>
            </div>
          </section>

          <section className="story-section technology-section">
            <h2>Innovation That Changes Everything</h2>
            <p>
              Today, through a state-of-the-art approach to shared kitchen spaces, Commissary Connect members have access to 
              <strong>around the clock dedicated workstations</strong>, shared office space, and modern appliances with 
              affordable rates based on a "pay-for-what-you-use" philosophy. Driving all of these innovations is Commissary 
              Connect's <strong>patented shared-use technology</strong> that enables online booking, tracks equipment usage 
              and maintenance, and powers Wi-Fi-enabled door locks, all in a collaborative and supportive atmosphere. Data 
              collection capabilities are also embedded directly into every aspect of operations.
            </p>
            <div className="story-image">
              <img src={sarbFoodTruck} alt="Sarb Mund serving from food truck" />
              <p className="image-caption">One of the many quarter stations in Commissary Connect's facilities<br/>c. 2021</p>
            </div>
          </section>

          <section className="story-section impact-section">
            <h2>The Numbers Tell the Story</h2>
            <div className="stats-grid">
              <div className="stat-item">
                <div className="stat-number">500+</div>
                <div className="stat-label">Businesses Launched</div>
              </div>
              <div className="stat-item">
                <div className="stat-number">100+</div>
                <div className="stat-label">Current Members</div>
              </div>
              <div className="stat-item">
                <div className="stat-number">5</div>
                <div className="stat-label">BC Locations</div>
              </div>
              <div className="stat-item">
                <div className="stat-number">12</div>
                <div className="stat-label">Sites in Development</div>
              </div>
            </div>
            <p className="impact-description">
              Commissary Connect's mission now focuses on connecting local food through technology, community, 
              support and resources. Currently there are close to 100 members at various stages of their food 
              business in all five Commissary Connect locations.
            </p>
            <p className="impact-description">
              Since opening their kitchens in 2012 to members, <strong>over 500 small business owners</strong> 
              have used the facilities, and many have had the extra benefit of tapping into Sarb and his team's business 
              expertise through their <strong>Entrepreneur-in-Residence program</strong>. For most, this has meant 
              expanding their business models, but for some, it has also meant shifting what they do and how they do it. 
              The goal has been, and continues to be, to help everyone succeed. Sometimes that means getting them to the 
              next level, and at other times, it means helping to identify an exit strategy.
            </p>
          </section>

          <section className="story-section future-section">
            <h2>The Vision Continues</h2>
            <p>
              The overall scope of Sarb's vision for Commissary Connect is always expanding. Next on their road to 
              expansion is creating a viable way to connect food hubs and commissaries all across the province to 
              each other, while also eventually increasing that reach nationwide.
            </p>
            
            <div className="video-section">
              <h3>See Our Impact in Action</h3>
              <div className="video-container">
                <iframe 
                  width="600" 
                  height="338" 
                  src="https://www.youtube.com/embed/2q19izZfwus" 
                  title="Food Hubs BC - Commissary Connect, Vancouver" 
                  frameBorder="0" 
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" 
                  referrerPolicy="strict-origin-when-cross-origin" 
                  allowFullScreen>
                </iframe>
              </div>
            </div>
          </section>
        </div>
        
        <div className="timeline">
          <h2>The Journey: From Food Truck to Provincial Pioneer</h2>
          <div className="timeline-items">
            <div className="timeline-item">
              <div className="timeline-year">2012</div>
              <div className="timeline-content">
                <h3>The Problem</h3>
                <p>Sarb struggles to find affordable kitchen space for Soho Road Naan Kebab food truck</p>
              </div>
            </div>
            <div className="timeline-item">
              <div className="timeline-year">2013</div>
              <div className="timeline-content">
                <h3>The Solution</h3>
                <p>Founded Commissary Connect with first location in East Vancouver</p>
              </div>
            </div>
            <div className="timeline-item">
              <div className="timeline-year">2017</div>
              <div className="timeline-content">
                <h3>Innovation</h3>
                <p>Opened specialized bakery location; patented pay-per-use technology</p>
              </div>
            </div>
            <div className="timeline-item">
              <div className="timeline-year">2019</div>
              <div className="timeline-content">
                <h3>Industry Leadership</h3>
                <p>Laurel Street facility opens as Canada's first Shared-Use HACCP-ready site</p>
              </div>
            </div>
            <div className="timeline-item">
              <div className="timeline-year">2020</div>
              <div className="timeline-content">
                <h3>Provincial Recognition</h3>
                <p>Created BC Food Hub Network for Ministry of Agriculture; BC's first Regional Food Hub</p>
              </div>
            </div>
            <div className="timeline-item">
              <div className="timeline-year">2024</div>
              <div className="timeline-content">
                <h3>Expanding the Network</h3>
                <p>5 locations across BC, 500+ businesses launched, building provincial food infrastructure</p>
              </div>
            </div>
          </div>
        </div>

        <div className="story-cta">
          <div className="cta-content">
            <h2>Ready to Be Part of This Story?</h2>
            <p>Join the network that has launched 500+ businesses and created BC's food hub infrastructure</p>
            <div className="cta-buttons">
              <a 
                href="/application" 
                data-apply-label="story_apply"
                className="btn-primary" 
                onClick={() => {
                  if (window.gtag) {
                    window.gtag('event', 'click', {
                      event_category: 'Application',
                      event_label: 'Story Page Start Journey',
                      value: 1
                    });
                  }
                }}
              >
                Start Your Journey
              </a>
              <Link to="/our-kitchens" className="btn-secondary">Tour Our Facilities</Link>
            </div>
            <p className="cta-note">From food trucks to restaurant chains – we've seen it all and helped it grow</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Story;